import {
  firstVisibleSublabel,
  isGroup,
  isNodeOpen,
  isNodeSynthetic,
} from '../util';
import { BlocksNode } from '../types';
import { Bounds } from '@ardoq/graph';

import {
  AQBLOCKS_EXPANDER_SIZE,
  AQBLOCKS_GROUPRD_SIZE,
  AQBLOCKS_LABEL_LINESIZE,
  AQBLOCKS_LEAFRD_SIZE,
  AQBLOCKS_NODE_MARGIN,
  AQBLOCKS_PAD,
  AQBLOCKS_SEP,
  AQBLOCKS_SUBLABEL_LINESIZE,
} from '../consts';
import { rectBloat } from 'tabview/visualizations/misc/geometry';
import { NodeComposition } from './types';

const pad = AQBLOCKS_PAD;
const sep = AQBLOCKS_SEP;
const margin = AQBLOCKS_NODE_MARGIN;
const rdheight = AQBLOCKS_LEAFRD_SIZE;

export const getDisplayLabelHeight = (node: BlocksNode) => {
  if (!node.displayLabels.length) {
    return 0;
  }

  return node.displayLabels[0].length * AQBLOCKS_LABEL_LINESIZE;
};

export const getDisplaySublabelsHeight = (node: BlocksNode) => {
  const first = firstVisibleSublabel(node);
  let ret = 0;
  let sublabelsCount = 0;

  for (let i = first; i < node.displayLabels.length; ++i) {
    if (node.displayLabels[i].length > 0) {
      ret += node.displayLabels[i].length * AQBLOCKS_SUBLABEL_LINESIZE;
      ++sublabelsCount;
    }
  }

  if (sublabelsCount > 1) {
    ret += (sublabelsCount - 1) * sep;
  }

  return ret;
};

const getClosedComposition = (
  node: BlocksNode,
  bounds: Bounds
): NodeComposition => {
  const labelheight = getDisplayLabelHeight(node);
  const sublabelheight = getDisplaySublabelsHeight(node);

  let header: Bounds | undefined = undefined;
  let expander: Bounds | undefined = undefined;
  let badge: Bounds | undefined = undefined;
  let label: Bounds | undefined = undefined;
  let rd: Bounds | undefined = undefined;
  let sublabels: Bounds | undefined = undefined;

  const body = rectBloat(bounds, -pad); // start with a padded "body"

  if (isGroup(node) && !isNodeOpen(node)) {
    header = [bounds[0], bounds[1], bounds[2], bounds[1] + 24];
    expander = [
      header[0] + pad,
      header[1] + pad,
      header[0] + pad + AQBLOCKS_EXPANDER_SIZE + pad,
      header[3] - pad,
    ];
    badge = [
      header[2] - pad - 16,
      header[1] + pad,
      header[2] - pad,
      header[3] - pad,
    ];

    body[1] = Math.max(header[3], expander[3], badge[3]) + sep; // tinker with padded body top to compensate for the header
  }

  if (sublabelheight > 0) {
    /* sublabels */
    sublabels = [body[0], body[3] - sublabelheight, body[2], body[3]];

    body[3] = sublabels[1] - sep; // tinker with the padded body bottom to compensate for the sublabels
  }

  /* center the combined rd and label within the body */
  // TODO if the rd rect isn't going to fit, squish it

  const h = rdheight + sep + labelheight; // height of the bit to center
  const t = (body[1] + body[3] - h) / 2; // top of the bit to center

  rd = [body[0], t, body[2], t + rdheight];
  label = [body[0], rd[3] + sep, body[2], rd[3] + sep + labelheight];

  return {
    bounds: bounds,
    header: header,
    expander: expander,
    badge: badge,
    rd: rd,
    label: label,
    sublabels: sublabels,
  };
};

const getOpenComposition = (
  node: BlocksNode,
  bounds: Bounds
): NodeComposition => {
  const padded = rectBloat(bounds, -pad);

  if (isNodeSynthetic(node)) {
    return {
      bounds: bounds,
      header: undefined,
      expander: undefined,
      rd: undefined,
      label: undefined,
      badge: undefined,
      sublabels: undefined,
    };
  }

  const labelheight = getDisplayLabelHeight(node);
  const sublabelsHeight = getDisplaySublabelsHeight(node);

  const rcExpander: Bounds = [
    padded[0],
    padded[1],
    padded[0] + AQBLOCKS_EXPANDER_SIZE,
    padded[1] + AQBLOCKS_EXPANDER_SIZE,
  ];
  const rcIcon: Bounds = [
    rcExpander[2] + sep,
    padded[1],
    rcExpander[2] + sep + AQBLOCKS_GROUPRD_SIZE,
    padded[1] + AQBLOCKS_EXPANDER_SIZE,
  ];
  const rcLabel: Bounds = [
    rcIcon[2] + sep,
    padded[1],
    padded[2],
    padded[1] + labelheight,
  ];
  const rcHeader: Bounds = [
    bounds[0],
    bounds[1],
    bounds[2],
    Math.max(rcExpander[3], rcIcon[3], rcLabel[3]) + pad,
  ];

  const rcSublabel: Bounds = [
    padded[0],
    rcHeader[3] + sep,
    padded[2],
    rcHeader[3] + sep + sublabelsHeight,
  ];

  return {
    bounds: bounds,
    header: rcHeader,
    expander: rcExpander,
    rd: rcIcon,
    label: rcLabel,
    badge: undefined,
    sublabels: rcSublabel,
  };
};

export const getOpenHeaderHeight = (node: BlocksNode) => {
  if (isNodeSynthetic(node)) {
    return 0;
  }

  const labelheight = getDisplayLabelHeight(node);
  const sublabelsHeight = getDisplaySublabelsHeight(node);

  return margin + labelheight + sep + sublabelsHeight + sep;
};

export const getComposition = (
  node: BlocksNode,
  bounds: Bounds
): NodeComposition => {
  return isGroup(node) && isNodeOpen(node)
    ? getOpenComposition(node, bounds)
    : getClosedComposition(node, bounds);
};
